import { ref } from 'vue'
import { defineStore } from 'pinia'
import { getDocument, listPublicDocuments } from '@/firebase/repository'
import {
  articles as seededArticles,
  carouselSlides as seededCarouselSlides,
  chatbotFlows as seededChatbotFlows,
  doctors as seededDoctors,
  galleryItems as seededGallery,
  insuranceProviders as seededInsuranceProviders,
  landingSections as seededSections,
  navigationConfig as seededNavigation,
  serviceCategories as seededCategories,
  services as seededServices,
  specialties as seededSpecialties,
  siteSettings as seededSettings,
} from '@/data/seed'
import type { Article, CarouselSlide, ChatbotFlow, Doctor, GalleryItem, InsuranceProvider, LandingSection, MedicalSpecialty, NavigationConfig, Service, ServiceCategory, SiteSettings } from '@/types/models'

function mergeByIdOrKey<T extends { id: string; key?: string; sortOrder?: number }>(seeded: T[], remote: T[]) {
  const byIdentity = new Map<string, T>()
  const identity = (item: T) => item.key || item.id
  for (const item of seeded) byIdentity.set(identity(item), item)
  for (const item of remote) {
    const current = byIdentity.get(identity(item))
    byIdentity.set(identity(item), current ? { ...current, ...item } : item)
  }
  return [...byIdentity.values()].sort((left, right) => Number(left.sortOrder ?? 0) - Number(right.sortOrder ?? 0))
}

export const useContentStore = defineStore('content', () => {
  const settings = ref<SiteSettings>({ ...seededSettings })
  const categories = ref<ServiceCategory[]>(seededCategories)
  const specialties = ref<MedicalSpecialty[]>(seededSpecialties)
  const services = ref<Service[]>(seededServices)
  const doctors = ref<Doctor[]>(seededDoctors)
  const articles = ref<Article[]>(seededArticles)
  const carouselSlides = ref<CarouselSlide[]>(seededCarouselSlides)
  const gallery = ref<GalleryItem[]>(seededGallery)
  const insuranceProviders = ref<InsuranceProvider[]>(seededInsuranceProviders)
  const sections = ref<LandingSection[]>(seededSections)
  const chatbotFlows = ref<ChatbotFlow[]>(seededChatbotFlows)
  const navigation = ref<NavigationConfig>({ ...seededNavigation, items: [...seededNavigation.items] })
  const loading = ref(false)
  const loaded = ref(false)
  const error = ref('')
  async function load() {
    if (loading.value || loaded.value) return
    loading.value = true
    error.value = ''
    try {
      const [remoteSettings, remoteCategories, remoteSpecialties, remoteServices, remoteDoctors, remoteArticles, remoteCarouselSlides, remoteGallery, remoteInsuranceProviders, remoteSections, remoteChatbotFlows, remoteNavigation] = await Promise.all([
        getDocument<SiteSettings>('siteSettings', 'main', seededSettings),
        listPublicDocuments('serviceCategories', seededCategories, 'active', true, 'sortOrder'),
        listPublicDocuments('specialties', seededSpecialties, 'active', true, 'sortOrder'),
        listPublicDocuments('services', seededServices, 'active', true, 'sortOrder'),
        listPublicDocuments('doctors', seededDoctors, 'active', true, 'sortOrder'),
        listPublicDocuments('articles', seededArticles, 'status', 'published'),
        listPublicDocuments('carouselSlides', seededCarouselSlides, 'active', true, 'sortOrder'),
        listPublicDocuments('galleryItems', seededGallery, 'active', true, 'sortOrder'),
        listPublicDocuments('insuranceProviders', seededInsuranceProviders, 'active', true, 'sortOrder'),
        listPublicDocuments('landingSections', seededSections, 'enabled', true, 'sortOrder'),
        listPublicDocuments('chatbotFlows', seededChatbotFlows, 'enabled', true, 'sortOrder'),
        getDocument<NavigationConfig>('navigation', 'main', seededNavigation),
      ])
      settings.value = { ...seededSettings, ...(remoteSettings || {}) }
      categories.value = remoteCategories
      specialties.value = remoteSpecialties.length
        ? remoteSpecialties.map((specialty) => {
          const seeded = seededSpecialties.find((item) => item.id === specialty.id)
          if (!seeded) return specialty
          const merged = { ...seeded, ...specialty }
          return specialty.animationHtmlUrl ? merged : {
            ...merged,
            visualType: seeded.animationHtmlUrl ? seeded.visualType : merged.visualType,
            primaryVisual: seeded.animationHtmlUrl ? seeded.primaryVisual : merged.primaryVisual,
            animationHtmlUrl: seeded.animationHtmlUrl,
            gameLinkEnabled: seeded.animationHtmlUrl ? true : merged.gameLinkEnabled,
            gameLinkUrl: seeded.animationHtmlUrl || merged.gameLinkUrl,
            gameLinkTooltip: seeded.gameLinkTooltip || merged.gameLinkTooltip,
          }
        })
        : seededSpecialties
      services.value = remoteServices
      doctors.value = remoteDoctors
      articles.value = remoteArticles
      carouselSlides.value = remoteCarouselSlides.length ? remoteCarouselSlides : seededCarouselSlides
      gallery.value = remoteGallery
      insuranceProviders.value = remoteInsuranceProviders.length ? mergeByIdOrKey(seededInsuranceProviders, remoteInsuranceProviders) : seededInsuranceProviders
      sections.value = remoteSections.length ? mergeByIdOrKey(seededSections, remoteSections) : seededSections
      chatbotFlows.value = remoteChatbotFlows.length ? remoteChatbotFlows : seededChatbotFlows
      navigation.value = remoteNavigation?.items?.length ? remoteNavigation : seededNavigation
      loaded.value = true
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : 'No fue posible cargar el contenido remoto.'
    } finally {
      loading.value = false
    }
  }

  function section(key: string) {
    return sections.value.find((item) => item.key === key && item.enabled)
  }

  return { settings, categories, specialties, services, doctors, articles, carouselSlides, gallery, insuranceProviders, sections, chatbotFlows, navigation, loading, loaded, error, load, section }
})
